import React, { useState } from 'react';
import cn from 'classnames';

import { selectIsLoggedIn } from 'client/redux/client/selectors';
import { selectNeedToRecord, selectRoomDbData, selectRoomStatus } from 'client/redux/room/selectors';
import { useAppSelector, useTranslation, useAppDispatch, useGAEventCommonProps } from 'client/hooks';
import { selectIsBroadcasting } from 'client/redux/mux-details/selectors';
import { setIsShowFooterPopUpOverlay } from 'client/redux/ui';
import { ROOM_STATUS } from 'constants/index';
import { analytics } from 'client/utils';
import { UserLoginType } from 'client/types';
import { setFormByUserType } from 'client/redux/login';

import logo from 'client/assets/roomContent/roomLogo.svg';

import Timer from '../Timer';
import Icon from '../Icon';
import TimeOutPopup from '../Popups/TimeOutPopup';
import LogoutPopup from '../Popups/LogoutPopup';
import TransitionComponent from '../TransitionComponent';

import css from './Header.module.scss';

interface Props {
  className?: string;
  roomId: string;
}

const Header: React.FC<Props> = ({ className = '' }) => {
  const { translate } = useTranslation('header');
  const roomData = useAppSelector(selectRoomDbData);
  const roomStatus = useAppSelector(selectRoomStatus);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const isBroadcasting = useAppSelector(selectIsBroadcasting);
  const isRoomRecording = useAppSelector(selectNeedToRecord);
  const dispatch = useAppDispatch();
  const { common } = useGAEventCommonProps();

  const [loginPopupActivity, setLoginPopupActivity] = useState<boolean>(false);

  const isRoomActive = roomStatus == ROOM_STATUS.ACTIVE;
  const isRoomFinished = roomStatus == ROOM_STATUS.ACTIVE;

  const onLoginPopupActivity = (isOpen: boolean) => (): void => {
    if (isOpen && isLoggedIn) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translate('logoutLabel'),
      });
    }

    if (isOpen && !isLoggedIn) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translate('loginLabel'),
      });
    }

    if (isLoggedIn) {
      setLoginPopupActivity(isOpen);
      dispatch(setIsShowFooterPopUpOverlay(false));
    } else {
      dispatch(setFormByUserType(UserLoginType.regular));
    }
  };

  return (
    <div className={cn(css.header, className)} data-testid="header">
      <div className={css.container}>
        {true && (
          <div className={css.login} onClick={onLoginPopupActivity(true)} data-testid="login-btn">
            {isLoggedIn ? (
              <>
                <Icon type="arrow_logout" />
                <span data-testid="logout-btn-label">{translate('logoutLabel')}</span>
              </>
            ) : (
              <>
                <Icon type="arrow_login" />
                <span data-testid="login-btn-label">{translate('loginLabel')}</span>
              </>
            )}
          </div>
        )}
        {(isRoomActive || isRoomFinished) && isLoggedIn && (
          <div className={css.login} data-testid="logout-btn" onClick={onLoginPopupActivity(true)}>
            <Icon type="arrow_logout" />
            <span>{translate('logoutLabel')}</span>
          </div>
        )}

        <img src={logo} alt="" />
        <span>{translate('label')}</span>

        {isBroadcasting && (
          <>
            <div className={cn(css.recording, isRoomRecording && css.recordingRoom)}>
              <div className={css.timer} data-testid="timer">
                <span className={css.timerCounter}>
                  {roomData && <Timer date={roomData.startedAt || roomData.date} />}
                </span>
                <Icon type="record" className={css.iconRecord} />
              </div>
              {isRoomRecording && isRoomActive && (
                <span data-testid="recording-info" className={css.recordingText}>
                  {translate('recordingText')}
                </span>
              )}
            </div>
          </>
        )}

        <TransitionComponent type="opacity" visible={loginPopupActivity && isLoggedIn}>
          <LogoutPopup onClose={onLoginPopupActivity(false)} />
        </TransitionComponent>

        <TimeOutPopup />
      </div>
    </div>
  );
};

export default Header;
